import React, { useState } from "react";
import { API_ENDPOINTS } from "../../config";
import { PAYMENT_CONSTANTS } from "../../constants/payment";

interface PaymentModalProps {
  onClose: () => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate price display from cents
  const priceDollars = Math.floor(PAYMENT_CONSTANTS.PRICE_CENTS / 100);
  const priceCents = PAYMENT_CONSTANTS.PRICE_CENTS % 100;

  const handleProceedToPayment = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Please log in to continue");
        setLoading(false);
        return;
      }

      // Call backend to create Stripe checkout session
      const response = await fetch(API_ENDPOINTS.PAYMENT_CREATE_CHECKOUT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to create checkout session");
      }

      const data = await response.json();

      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      console.error("Payment error:", err);
      setError(err.message || "Failed to initiate payment. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="payment-modal-overlay" onClick={onClose}>
      <div className="payment-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="payment-modal-close" onClick={onClose} disabled={loading}>
          ✕
        </button>

        <div className="payment-modal-header">
          <h2>Unlock Full Access</h2>
          <p className="payment-modal-subtitle">
            Get complete therapist profiles and contact information
          </p>
        </div>

        {error && (
          <div style={{
            background: "#fee",
            border: "1px solid #fcc",
            borderRadius: "8px",
            padding: "12px",
            marginBottom: "1rem",
            color: "#c33",
            fontSize: "0.9rem",
          }}>
            {error}
          </div>
        )}

        <div className="payment-modal-features">
          <div className="payment-feature">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17L4 12" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>View full therapist names and credentials</span>
          </div>
          <div className="payment-feature">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17L4 12" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Access profile URLs and contact details</span>
          </div>
          <div className="payment-feature">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17L4 12" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Unlimited access to all recommendations</span>
          </div>
          <div className="payment-feature">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17L4 12" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>One-time payment, lifetime access</span>
          </div>
        </div>

        <div className="payment-modal-pricing">
          <div className="payment-price">
            <span className="payment-currency">$</span>
            <span className="payment-amount">{priceDollars}</span>
            <span className="payment-period">.{priceCents.toString().padStart(2, '0')}</span>
          </div>
          <p className="payment-price-description">One-time payment • No subscription</p>
        </div>

        <button
          className="payment-modal-button"
          onClick={handleProceedToPayment}
          disabled={loading}
          style={{
            opacity: loading ? 0.7 : 1,
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Redirecting to Stripe..." : "Proceed to Payment"}
        </button>

        <p className="payment-modal-secure">
          🔒 Secure payment powered by Stripe
        </p>
      </div>
    </div>
  );
};

export default PaymentModal;
